import axios from 'axios';

const HEADER_NAME = 'x-csrf-token';
const FIELD_NAME = '_csrf';

let _responseInterceptorId = null;

export function getToken() {
  return axios.defaults.headers.common[HEADER_NAME] || '';
}

export function setToken(value) {
  if (value) {
    delete axios.defaults.headers.common['X-CSRF-Token'];
    axios.defaults.headers.common[HEADER_NAME] = value;
  }
}

function isSameOrigin(url) {
  try {
    const resolved = new URL(url, window.location.origin);
    return resolved.origin === window.location.origin;
  } catch {
    return false;
  }
}

function setupAxiosInterceptors() {
  if (_responseInterceptorId !== null) {
    axios.interceptors.response.eject(_responseInterceptorId);
  }

  _responseInterceptorId = axios.interceptors.response.use(
    (response) => {
      const newToken = response.headers[HEADER_NAME];
      if (newToken) {
        setToken(newToken);
      }
      return response;
    },
    (error) => {
      if (!error.response) {
        return Promise.reject(error);
      }

      const newToken = error.response.headers[HEADER_NAME];
      if (newToken) {
        setToken(newToken);
      }

      if (error.response.status === 403 && !error.config._csrfRetry && error.config) {
        error.config._csrfRetry = true;
        delete error.config.headers[HEADER_NAME];
        return axios(error.config);
      }

      return Promise.reject(error);
    }
  );
}

let _fetchPatched = false;

function setupFetchInterceptor() {
  if (_fetchPatched || typeof window === 'undefined' || !window.fetch) return;
  _fetchPatched = true;

  const originalFetch = window.fetch;

  window.fetch = function(input, init = {}) {
    const url = typeof input === 'string' ? input : input.url || '';

    if (isSameOrigin(url)) {
      const token = getToken();
      if (token) {
        const headers = new Headers(init.headers || {});
        if (!headers.has(HEADER_NAME)) {
          headers.set(HEADER_NAME, token);
        }
        init = { ...init, headers };
      }
    }

    return originalFetch.call(window, input, init).then((response) => {
      const newToken = response.headers.get(HEADER_NAME);
      if (newToken) {
        setToken(newToken);
      }
      return response;
    });
  };
}

let _formListenerAdded = false;

function setupFormInterceptor() {
  if (_formListenerAdded || typeof document === 'undefined') return;
  _formListenerAdded = true;

  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!form || form.tagName !== 'FORM') return;

      const method = (form.method || 'GET').toUpperCase();
      if (method !== 'POST') return;

      const token = getToken();
      if (!token) return;

      let input = form.querySelector(`input[name="${FIELD_NAME}"]`);
      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = FIELD_NAME;
        form.appendChild(input);
      }
      input.value = token;
    },
    true
  );
}

export function initCsrf(token) {
  setToken(token);
  setupAxiosInterceptors();
  setupFetchInterceptor();
  setupFormInterceptor();
}
